<template>
  <div class="hr-project-details">
    <div class="hr-project-details__header">
      <div class="hr-project-details__title-container">
        <h2 class="hr-project-details__title">Project information</h2>
      </div>
      <v-btn
        class="hr-project-details__toggle-btn"
        :class="{ 'hr-project-details__toggle-btn--open': isContentVisible }"
        fab
        x-small
        depressed
        color="blue-light"
        @click="toggleVisibility"
      >
        <v-icon color="blue">mdi-chevron-down</v-icon>
      </v-btn>
    </div>

    <v-slide-y-transition>
      <div
        v-show="isContentVisible"
        class="hr-project-details__content"
      >
        <div class="hr-project-details__base-info">
          <div class="hr-project-details__avatar-wrapper">
            <div class="hr-project-details__avatar">
              <AvatarPlaceholder :name="projectData.name" />
            </div>
          </div>

          <div class="hr-project-details__data">
            <h2 class="hr-project-details__name">
              {{ projectData.name }}
            </h2>
            <p class="hr-project-details__client">
              {{ projectData.client_name }}
            </p>

            <div v-if="projectData.start_date" class="project-details">
              <div class="project-details__icon">
                <img src="@/assets/img/icon-project-details.svg" alt="Bench logo">
              </div>
              <div class="project-details__wrapper">
                <label class="project-details__label">Project term</label>
                <p class="project-details__data">
                  {{ projectData.start_date | displayDate }}
                  -
                  {{ projectData.end_date | displayDate }}
                </p>
              </div>
            </div>

            <div v-if="projectData.team_size" class="project-details">
              <div class="project-details__icon">
                <img src="@/assets/img/icon-team-members.svg" alt="Bench logo">
              </div>
              <div class="project-details__wrapper">
                <label class="project-details__label">Team size</label>
                <p class="project-details__data">
                  {{ teamSizeDisplay }} members
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="hr-project-details__features">
          <v-row>
            <v-col class="col-12 col-lg-6">
              <h3 class="hr-project-details__group-title">
                Technologies
              </h3>
              <FeaturesList
                v-if="projectData.technologies && projectData.technologies.length"
                :features="projectData.technologies"
                primary
                :editable="false"
              />
            </v-col>
            <v-col class="col-12 col-lg-6">
              <h3 class="hr-project-details__group-title">
                Management methodologies
              </h3>
              <FeaturesList
                v-if="projectData.management && projectData.management.length"
                :features="projectData.technologies"
                :editable="false"
              />
            </v-col>
            <v-col class="col-12 col-lg-6">
              <h3 class="hr-project-details__group-title">
                Project areas
              </h3>
              <FeaturesList
                v-if="projectData.project_areas && projectData.project_areas.length"
                :features="projectData.project_areas"
                :editable="false"
              />
            </v-col>
          </v-row>
        </div>
      </div>
    </v-slide-y-transition>
  </div>
</template>

<script>
import FeaturesList from '@/components/Developers/Show/FeaturesList.vue';
import AvatarPlaceholder from '@/components/AvatarPlaceholder.vue';

export default {
  components: {
    FeaturesList,
    AvatarPlaceholder,
  },

  props: {
    projectData: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isContentVisible: true,
    };
  },

  computed: {
    teamSizeDisplay() {
      let displaySize;
      switch (this.projectData.team_size) {
        case 'small':
          displaySize = '1-5';
          break;
        case 'medium':
          displaySize = '6-9';
          break;
        case 'large':
          displaySize = '10+';
          break;
        default:
          break;
      }
      return displaySize;
    },
  },

  methods: {
    toggleVisibility() {
      this.isContentVisible = !this.isContentVisible;
    },
  },
};
</script>

<style lang="scss" scoped>
.hr-project-details {
  margin-bottom: 24px;
  padding: 16px 24px;
  background: #FFFFFF;
  box-shadow: 0px 3px 6px #00000029;
  border-radius: 8px;
  transition: all 0.4s ease;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__title {
    font-size: 18px;
    font-weight: 500;
    line-height: 21px;
    color: $main--headers;
  }

  &__toggle-btn {
    transition: all 0.4s ease;
    &--open {
      transform: rotate(180deg);
    }
  }

  &__base-info {
    margin-top: 16px;
    display: flex;
    flex-direction: column;

    @include tablet {
      flex-direction: row;
    }
  }

  &__avatar-wrapper {
    text-align: center;
  }

  &__avatar {
    background: $color--secondary;
    width: 128px;
    height: 128px;
    border-radius: 8px;
    margin: 0 auto;
  }

  &__data {
    flex-grow: 1;
    margin: 16px 0;

    @include tablet {
      margin: 0 0 0 24px;
    }
  }

  &__name {
    color: $main--headers;
    font-size: 20px;
    line-height: 24px;
    font-weight: bold;
    a {
      text-decoration: unset !important;
      color: $main--headers;
    }
  }

  &__client {
    color: $color--gray;
    font-size: 14px;
    font-weight: bold;
    line-height: 16px;
    margin-bottom: 4px;
  }

  &__features {
    margin-top: 16px;
  }

  &__group-title {
    margin-bottom: 12px;
    font-weight: 500;
    color: $main--headers;
  }
}

.project-details {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0px;
  }

  &__icon {
    margin-right: 8px;
    margin-top: 10px;
  }

  &__label {
    font-size: 10px;
    line-height: 12px;
    margin-bottom: 0;
  }
  &__data {
    font-size: 14px;
    line-height: 14px;
    margin-bottom: 0;
  }
}
</style>
